import React, { useState } from 'react';

interface CreateGameProps {
  onGameCreated: (code: string) => void;
  onCancel: () => void;
}

export const CreateGame: React.FC<CreateGameProps> = ({ onGameCreated, onCancel }) => {
  const [theme, setTheme] = useState<string>('');
  const [words, setWords] = useState<string[]>(['', '', '', '', '']);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const handleWordChange = (index: number, value: string) => {
    const newWords = [...words];
    newWords[index] = value;
    setWords(newWords);
  };

  const addWordInput = () => {
    setWords([...words, '']);
  };

  const removeWordInput = (index: number) => {
    if (words.length <= 3) return; // Minimum 3 words
    const newWords = [...words];
    newWords.splice(index, 1);
    setWords(newWords);
  };

  const handleCreateGame = () => {
    // Validate inputs
    if (!theme || words.some(word => !word)) {
      alert('Please fill in the theme and all word fields.');
      return;
    }

    // Validate minimum word length
    if (words.some(word => word.length < 4)) {
      alert('All words must be at least 4 characters long.');
      return;
    }

    // Validate maximum word count
    if (words.length > 10) {
      alert('Please limit your puzzle to 10 words maximum.');
      return;
    }

    setIsCreating(true);

    // For a basic implementation, we'll just encode the game data in base64
    const gameData = {
      theme,
      words,
    };

    const gameCode = btoa(JSON.stringify(gameData));

    // Simulate API call delay
    setTimeout(() => {
      setIsCreating(false);
      onGameCreated(gameCode);
    }, 1000);
  };

  return (
    <div className="w-full h-full flex flex-col p-2 pl-0 md:p-6">
      <div className="flex items-center mb-6">
        <button
          onClick={onCancel}
          className="btn !pl-3 text-white font-bold relative bottom-0.5 !transform-none !shadow-none"
          aria-label="Back to menu"
        >
          {"<"}
        </button>
        <h1 className="font-raleway text-2xl font-extrabold text-white uppercase">
          Strands<span className="text-selected">.</span>
        </h1>
      </div>

      <div className="flex-1 flex flex-col overflow-y-auto pb-4">
        <div className="bg-white p-6 rounded-lg shadow-md flex-1 overflow-y-auto">
          <div className="mb-2 text-sm text-gray-700 bg-blue-50 p-3 rounded-md">
            <p className="font-semibold">How to play Strands:</p>
            <ul className="list-disc ml-5 mt-1">
              <li>Find words in a 6x8 grid by connecting adjacent letters</li>
              <li>Letters can connect horizontally, vertically, or diagonally</li>
              <li>All words relate to a common theme</li>
              <li>All words must be at least 4 letters long</li>
            </ul>
          </div>

          <div className="mb-6 mt-4">
            <label className="block text-gray-700 font-semibold mb-2">
              Theme
            </label>
            <input
              type="text"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="e.g., Fruits, Countries, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-gray-700 font-semibold">
                Words (in a thematic set)
              </label>
              <button
                onClick={addWordInput}
                className="text-sm bg-blue-500 text-white py-1 px-2 rounded hover:bg-blue-600"
                disabled={words.length >= 10}
              >
                + Add Word
              </button>
            </div>

            {words.map((word, index) => (
              <div key={index} className="flex items-center mb-2">
                <input
                  type="text"
                  value={word}
                  onChange={(e) => handleWordChange(index, e.target.value)}
                  placeholder={`Word ${index + 1} (min 4 chars)`}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {words.length > 3 && (
                  <button
                    onClick={() => removeWordInput(index)}
                    className="ml-2 text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <p className="text-sm text-gray-500 mt-1">
              Enter words that all relate to the theme. Each word must have at least 4 characters. We recommend 5-8 words for the best gameplay experience.
            </p>
          </div>

          <div className="flex justify-end space-x-4 w-full mt-auto">
            <button
              onClick={handleCreateGame}
              disabled={isCreating}
              className="btn font-bold bg-white text-black"
            >
              {isCreating ? 'Creating...' : 'Create Game'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateGame;
